import _ from "lodash";
import React, { useState } from "react";
import classes from "./StudentActivities.scss";
import classNames from "classnames";
import moment from "moment";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import ConditionalTooltip from "Post/components/ConditionalTooltip";
import { colors, AVATAR_COLORS } from "Constants/colorConstants";
import { Avatar } from "@toddle-edu/ds-web";
import {
  getStringAutoLinks,
  hashString,
  isSafari,
  onEnterOrSpaceKeyDown,
} from "Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import ScrollableCarousel from "UIComponents/ScrollableCarousel";
import { ACTIVITY_NAP } from "Post/modules/PostModule";
import { useI18n, useMediaQuery } from "Hooks";
import {
  getFormattedUserNameWithoutPronoun,
  getIconUrl,
  isPortfolioParityEnabled,
} from "Post/utils";
import { TABLET_MEDIA_QUERY } from "Constants/stringConstants";

const extensionTextStyle = { fontSize: "1.2rem", marginTop: "2px" };

const PreviewElementRatings = ({
  ibPYPElementRatings,
  isSidePaneContent,
  isSingleActivity,
  isStudentFeedSmallCard,
}) => {
  const isMobile = useMediaQuery(TABLET_MEDIA_QUERY);
  if (_.size(ibPYPElementRatings) === 0) return null;

  const ratingsContainer = classNames({
    [classes.ratingsContainer]: true,
    [classes.horizontalRatings]: !isSidePaneContent || isStudentFeedSmallCard,
    [classes.singleActivityRatingsContainer]:
      isSingleActivity && !isSidePaneContent && !isStudentFeedSmallCard,
  });

  return (
    <div
      className={ratingsContainer}
      data-test-id="journal-activity-student-rating-container"
    >
      {_.map(ibPYPElementRatings, (rating, index) => (
        <div
          key={_.get(rating, "id")}
          className={classNames({
            [classes.rating]: true,
            [classes.feedRatingCard]: !isSidePaneContent,
            [classes.ratingFirstInRow]:
              !isSidePaneContent &&
              (isSingleActivity
                ? index === 0 || (isMobile && index % 2 === 0)
                : index % 2 === 0),
            [classes.ratingLastInRow]:
              !isSidePaneContent &&
              (isSingleActivity
                ? index === _.size(ibPYPElementRatings) - 1
                : index % 2 !== 0),
            [classes.multiColumnRating]:
              !isSidePaneContent &&
              !isSingleActivity &&
              index === 2 &&
              _.size(ibPYPElementRatings) - 1 === 2,
            [classes.multiColumnRatingV2]:
              isStudentFeedSmallCard && index === 2,
          })}
          data-test-id="journal-activity-student-rating"
        >
          <span
            className={classes.ratingIconContainer}
            style={
              !_.isNil(
                _.get(rating, "academicCriteriaValue.metadata.backgroundColor")
              )
                ? {
                    backgroundColor: _.get(
                      rating,
                      "academicCriteriaValue.metadata.backgroundColor",
                      ""
                    ),
                  }
                : {}
            }
          >
            {isSafari() ? (
              <object
                type="image/svg+xml"
                data={getIconUrl(
                  _.get(rating, "academicCriteriaValue.metadata.iconLink", "")
                )}
                className={classes.object}
              >
                {_.get(rating, "academicCriteriaValue.label", "")}
              </object>
            ) : (
              <img
                src={getIconUrl(
                  _.get(rating, "academicCriteriaValue.metadata.iconLink", "")
                )}
                alt={_.get(rating, "academicCriteriaValue.label", "")}
                className={classes.object}
              />
            )}
          </span>
          <div>
            <ConditionalTooltip
              placement={"top"}
              tooltip={_.get(rating, "academicCriteriaSet.label", "")}
            >
              <span className={classes.ratingsSubText}>
                {_.get(rating, "academicCriteriaSet.label", "")}
              </span>
            </ConditionalTooltip>
            <ConditionalTooltip
              tooltip={_.get(rating, "academicCriteriaValue.label", "")}
            >
              <span className={classes.ratingsText}>
                {_.get(rating, "academicCriteriaValue.label", "")}
              </span>
            </ConditionalTooltip>
          </div>
        </div>
      ))}
    </div>
  );
};

const PreviewDescription = ({
  description,
  isOnlyDescriptionPresent,
  isSingleActivity,
}) => {
  const ratingsDescription = classNames({
    [classes.ratingsDescription]: true,
    [classes.expandRatingsDescription]: isOnlyDescriptionPresent,
    [classes.singleActivityDescription]: isSingleActivity,
  });

  return (
    <span
      className={ratingsDescription}
      data-test-id="journal-activity-student-description"
      dangerouslySetInnerHTML={{ __html: getStringAutoLinks(description) }}
    />
  );
};

const PreviewAttachments = ({
  studentId,
  attachments,
  isSingleActivity,
  mediaPlaceholderProps,
  onMediaClick,
  handleActivitySelectedStudentChange,
  carouselWrapperStyle = {},
  showAttachmentCounterTag = false,
}) => {
  const handleMediaClick = ({ event, activeIndex }) => {
    if (
      !_.isFunction(onMediaClick) ||
      !_.isFunction(handleActivitySelectedStudentChange)
    )
      return;

    event?.stopPropagation();
    handleActivitySelectedStudentChange(studentId);
    onMediaClick({ activeIndex });
  };
  const [isAttachmentCountVisible, setAttachmentCountVisible] = useState(false);
  const limitedAttachments = _.take(attachments, 3);
  const attachmentGroup = classNames({
    [classes.attachmentContainer]: !isSingleActivity,
    [classes.singleActivityAttachmentScroll]: isSingleActivity,
  });
  const scrollAmount = isPortfolioParityEnabled() ? 564 + 8 : 536 + 8;
  return (
    <ConditionalWrapper
      condition={isSingleActivity && _.size(attachments) > 1}
      wrapper={
        <ScrollableCarousel
          scrollAmount={scrollAmount}
          leftButtonStyle={{ left: "16px" }}
          rightButtonStyle={{ right: "16px" }}
          carouselWrapperStyle={carouselWrapperStyle}
          setAttachmentCountVisible={setAttachmentCountVisible}
          isAttachmentCountVisible={isAttachmentCountVisible}
          showControlDots={isPortfolioParityEnabled()}
        />
      }
      type={"wrapperAsElement"}
    >
      <div className={attachmentGroup}>
        {_.map(
          isSingleActivity ? attachments : limitedAttachments,
          (attachment, index) => (
            <div className={classes.attachmentItemContainer}>
              <MediaPlaceholder
                key={index}
                attachments={[attachment]}
                onClickMedia={({ event }) =>
                  handleMediaClick({ event, activeIndex: index })
                }
                {...mediaPlaceholderProps}
                isAttachmentCountVisible={isAttachmentCountVisible}
                showAttachmentCounterTag={showAttachmentCounterTag}
                attachmentIndex={index}
                attachmentSize={_.size(attachments)}
              />
            </div>
          )
        )}
      </div>
    </ConditionalWrapper>
  );
};

export const StudentActivity = ({
  activityData,
  isSingleActivity = false,
  handleActivitySelectedStudentChange,
  activityContianerStyle = {},
  onMediaClick,
  isSidePaneContent = false,
  carouselWrapperStyle = {},
  showAttachmentCounterTag = false,
  isStudentFeedSmallCard = false,
}) => {
  const { t } = useI18n(["journal"]);

  const getSelectedStudentAttachments = selectedStudentActivityData => {
    let allAttachments = [];

    if (!_.isNil(selectedStudentActivityData)) {
      const attachmentsGroups = _.get(
        selectedStudentActivityData,
        "attachmentGroups",
        []
      );
      _.forEach(attachmentsGroups, group => {
        const attachments = _.get(group, "attachments", []);
        allAttachments = _.concat(allAttachments, attachments);
      });
    }
    return allAttachments;
  };

  const activityType = _.get(activityData, "activityType", "");
  const studentDetails = _.get(activityData, "activityStudent", {});
  let ibPYPElementRatings = _.cloneDeep(
    _.get(activityData, "ibPYPElementRatings", [])
  );
  const attachments = getSelectedStudentAttachments(activityData);

  if (activityType === ACTIVITY_NAP) {
    const startTime = _.get(activityData, "startTime", "");
    const endTime = _.get(activityData, "endTime", "");
    const ratings = [];

    if (!_.isEmpty(startTime)) {
      ratings.push({
        id: `${ACTIVITY_NAP}_${startTime}_${_.get(studentDetails, "id", "")}`,
        academicCriteriaSet: {
          label: t("journal:nap_started"),
          displaySequence: -2,
        },
        academicCriteriaValue: {
          label: moment(startTime).format("hh:mm A"),
          metadata: {
            iconLink: getIconUrl(
              "/assets/webapp/activity_nap_start_end_time.svg"
            ),
          },
        },
      });
    }

    if (!_.isEmpty(endTime)) {
      ratings.push({
        id: `${ACTIVITY_NAP}_${endTime}_${_.get(studentDetails, "id", "")}`,
        academicCriteriaSet: {
          label: t("journal:nap_ended"),
          displaySequence: -1,
        },
        academicCriteriaValue: {
          label: moment(endTime).format("hh:mm A"),
          metadata: {
            iconLink: getIconUrl(
              "/assets/webapp/activity_nap_start_end_time.svg"
            ),
          },
        },
      });
    }

    ibPYPElementRatings = _.concat(ratings, ibPYPElementRatings);
  }

  ibPYPElementRatings = _.orderBy(
    ibPYPElementRatings,
    ["academicCriteriaSet.displaySequence"],
    ["asc"]
  );

  const limitedIbPYPElementRatings = !isSidePaneContent
    ? _.take(ibPYPElementRatings, activityType === ACTIVITY_NAP ? 4 : 2)
    : ibPYPElementRatings;

  const showDescription = () => {
    const shouldShow =
      !isSidePaneContent && !_.isEmpty(_.get(activityData, "description"));
    if (!shouldShow) return null;

    return (
      <PreviewDescription
        isOnlyDescriptionPresent={
          _.size(ibPYPElementRatings) === 0 && _.size(attachments) === 0
        }
        description={_.get(activityData, "description")}
        isSingleActivity={isSingleActivity}
      />
    );
  };

  const onClickActivity = () => {
    if (_.isFunction(handleActivitySelectedStudentChange)) {
      handleActivitySelectedStudentChange(_.get(studentDetails, "id", ""));
    }
  };

  const onActivityCardKeydown = onClickActivity => e => {
    onEnterOrSpaceKeyDown(e, onClickActivity);
  };

  const showAttachments = () => {
    const shouldShow = !isSidePaneContent && !_.isEmpty(attachments);
    if (!shouldShow) return null;
    const activitiyImageMaxWidth = isPortfolioParityEnabled() ? 564 : 536;
    const commonMediaPlaceholderProps = {
      showOverlayIcons: false,
      showMiddleContainerIcons: false,
      showIconCardsWithExtension: true,
      showExtensionOnCornerByHover: isSingleActivity ? true : false,
    };
    const mediaPlaceholderProps = {
      imageMaxWidth: !isSingleActivity ? 116 : activitiyImageMaxWidth,
      imageMaxHeight: !isSingleActivity ? 64 : 350,
      containerStyle: {
        overflow: "visible",
        width: !isSingleActivity ? "116px" : `${activitiyImageMaxWidth}px`,
        height: !isSingleActivity ? "64px" : "350px",
      },
      itemStyles: {
        border: `1px solid ${colors.borderSubtle}`,
        borderRadius: "8px",
        width: !isSingleActivity ? "116px" : `${activitiyImageMaxWidth}px`,
        height: !isSingleActivity ? "64px" : "350px",
      },
      fileIconSize: !isSingleActivity ? "xx-small" : "small",
      extensionTextStyle: !isSingleActivity
        ? extensionTextStyle
        : {
            ...extensionTextStyle,
            fontSize: "1.4rem",
          },
    };

    return (
      <PreviewAttachments
        isSingleActivity={isSingleActivity}
        attachments={attachments}
        mediaPlaceholderProps={{
          ...commonMediaPlaceholderProps,
          ...mediaPlaceholderProps,
        }}
        studentId={_.get(studentDetails, "id", "")}
        handleActivitySelectedStudentChange={
          handleActivitySelectedStudentChange
        }
        onMediaClick={onMediaClick}
        showAttachmentCounterTag={showAttachmentCounterTag}
        carouselWrapperStyle={carouselWrapperStyle}
      />
    );
  };

  const activityContainer = classNames({
    [classes.activityContianer]: true,
    [classes.removeBorderAndWidth]: isSingleActivity,
    [classes.cursorPointer]: _.isFunction(handleActivitySelectedStudentChange),
  });
  return (
    <div
      data-test-id="journal-student-activity-card"
      tabIndex={0}
      className={activityContainer}
      style={activityContianerStyle}
      onClick={onClickActivity}
      onKeyDown={onActivityCardKeydown(onClickActivity)}
    >
      {!isSingleActivity ? (
        <div className={classes.profileContainer}>
          <Avatar
            shade={"light"}
            size={"small"}
            color={
              AVATAR_COLORS[
                hashString(_.get(studentDetails, "id", "")) %
                  AVATAR_COLORS.length
              ]
            }
            name={getFormattedUserNameWithoutPronoun({
              user: studentDetails,
              type: "student",
            })}
            src={_.get(studentDetails, "profileImage", "")}
          />
          <ConditionalTooltip
            placement={"top"}
            tooltip={getFormattedUserName({
              user: studentDetails,
            })}
          >
            <span
              className={classes.profileText}
              data-test-id="journal-activity-student-name"
            >
              {getFormattedUserName({
                user: studentDetails,
                type: "student",
              })}
            </span>
          </ConditionalTooltip>
        </div>
      ) : null}

      {showDescription()}
      <PreviewElementRatings
        ibPYPElementRatings={limitedIbPYPElementRatings}
        isSingleActivity={isSingleActivity}
        isSidePaneContent={isSidePaneContent}
        isStudentFeedSmallCard={isStudentFeedSmallCard}
      />
      {showAttachments()}
    </div>
  );
};

const StudentActivities = ({
  studentActivities,
  handleActivitySelectedStudentChange,
  activitiesContainerStyle = {},
  activityContianerStyle = {},
  leftButtonStyle = {},
  rightButtonStyle = {},
  scrollAmount,
  onMediaClick,
  defaultStudentNameOrderPreference,
  carouselWrapperStyle = {},
  showAttachmentCounterTag = false,
  isStudentFeedSmallCard = false,
}) => {
  if (_.size(studentActivities) === 1)
    return (
      <StudentActivity
        activityData={_.get(studentActivities, "0.item", {})}
        isSingleActivity={true}
        handleActivitySelectedStudentChange={
          handleActivitySelectedStudentChange
        }
        activityContianerStyle={activityContianerStyle}
        onMediaClick={onMediaClick}
        defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
        carouselWrapperStyle={carouselWrapperStyle}
        showAttachmentCounterTag={showAttachmentCounterTag}
        isStudentFeedSmallCard={isStudentFeedSmallCard}
      />
    );

  return (
    <ScrollableCarousel
      // card width: 256px; gap: 24px
      scrollAmount={!_.isNil(scrollAmount) ? scrollAmount : 366 + 16}
      leftButtonStyle={
        !_.isEmpty(leftButtonStyle) ? leftButtonStyle : { left: "-16px" }
      }
      rightButtonStyle={
        !_.isEmpty(rightButtonStyle) ? rightButtonStyle : { right: "-16px" }
      }
      testId="journal-student-activities-carousel"
    >
      <div
        className={classes.activitiesContainer}
        style={activitiesContainerStyle}
      >
        {_.map(studentActivities, activityData => (
          <StudentActivity
            key={_.get(activityData, "item.id")}
            activityData={_.get(activityData, "item", {})}
            handleActivitySelectedStudentChange={
              handleActivitySelectedStudentChange
            }
            activityContianerStyle={activityContianerStyle}
            onMediaClick={onMediaClick}
            defaultStudentNameOrderPreference={
              defaultStudentNameOrderPreference
            }
            isStudentFeedSmallCard={isStudentFeedSmallCard}
          />
        ))}
      </div>
    </ScrollableCarousel>
  );
};

export default StudentActivities;
